import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTimer } from "react-timer-hook";
import OtpInput from "react-otp-input";

import { useGenerateOtp } from "store/backend/apiHooks";
import { useLoginWithOtp } from "store/backend/useLogin";
import ReCAPTCHA from "react-google-recaptcha-enterprise";
import useAuth from "../../hooks/useAuth";
import { PrimaryButton, Textfield } from "widgets";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { SITE_KEY } from "constants/config";
import { ERROR_MESSAGES } from "constants/errorMessages";
import {
  DEFAULT_ISD_CODE,
  getPhoneConfigForIsd,
  isValidPhoneForIsd,
} from "constants/phoneConfig";

import styles from "./Login.module.scss";

const LOGIN_ISD_CODE = DEFAULT_ISD_CODE;

const handleGenerateOtpClick = async ({
  phone,
  isdCode,
  recaptchaRef,
  setStatus,
  generateOtp,
  openSnackbar,
  setStep,
  restart,
}) => {
  if (!isValidPhoneForIsd(phone, isdCode)) {
    setStatus(ERROR_MESSAGES.PHONE_INVALID);
    return;
  }

  try {
    setStatus("");

    const captchaToken = await recaptchaRef.current.executeAsync(
      "generate_otp"
    );
    recaptchaRef.current.reset();

    generateOtp(
      { phone, isd_code: isdCode, captcha_token: captchaToken },
      {
        onSuccess: () => {
          openSnackbar(ERROR_MESSAGES.OTP_SENT_SUCCESS, "success");
          setStep("OTP");

          const newExpiry = new Date();
          newExpiry.setSeconds(newExpiry.getSeconds() + 60);
          restart(newExpiry);
        },
        onError: (error) => {
          openSnackbar(
            error?.message || ERROR_MESSAGES.OTP_SEND_FAILED,
            "error"
          );
        },
      }
    );
  } catch (err) {
    setStatus(err?.message || ERROR_MESSAGES.CAPTCHA_FAILED);
  }
};

const extractGatewayTenantIds = (loginData) => {
  if (!Array.isArray(loginData?.verticals)) return [];

  const tenantIds = loginData.verticals.flatMap((vertical) =>
    Array.isArray(vertical?.tenant_ids) ? vertical.tenant_ids : []
  );

  return [...new Set(tenantIds.filter(Boolean))];
};

const handleLoginClick = ({
  otp,
  phone,
  isdCode,
  setStatus,
  loginWithOtp,
  authLogin,
  navigate,
  openSnackbar,
}) => {
  if (otp.trim().length < 4) {
    setStatus(ERROR_MESSAGES.OTP_INVALID);
    return;
  }

  loginWithOtp(
    { phone, otp, isd_code: isdCode },
    {
      onSuccess: (data) => {
        authLogin(data.access_token);
        localStorage.setItem("cashierCRNId", data.crn_id);
        localStorage.setItem("roles", data.roles);
        localStorage.setItem("cashierPhoneNumber", phone);
        const gatewayTenantIds = extractGatewayTenantIds(data);

        if (gatewayTenantIds.length > 0) {
          localStorage.setItem(
            "gatewayTenantIds",
            JSON.stringify(gatewayTenantIds)
          );
        }
        openSnackbar(ERROR_MESSAGES.LOGIN_SUCCESS, "success");
        navigate("/homePage");
      },
      onError: (error) => setStatus(error.message),
    }
  );
};

const OtpLogin = () => {
  const { openSnackbar } = useSnackbarContext();
  const { login: authLogin } = useAuth();
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);

  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("PHONE");
  const [status, setStatus] = useState("");

  const { mutate: generateOtp, isPending: isOtpLoading } = useGenerateOtp();
  const { mutate: loginWithOtp, isPending: isLoggingIn } = useLoginWithOtp();

  const initialExpiry = new Date();
  initialExpiry.setSeconds(initialExpiry.getSeconds());

  const { seconds, restart } = useTimer({
    expiryTimestamp: initialExpiry,
    autoStart: false,
  });

  const maxPhoneLength = getPhoneConfigForIsd(LOGIN_ISD_CODE).maxLength;
  const canRequestOtp = isValidPhoneForIsd(phone, LOGIN_ISD_CODE);
  const displayPhone = `${LOGIN_ISD_CODE} ${phone}`;

  return (
    <div className={styles.mainContainer}>
      <ReCAPTCHA sitekey={SITE_KEY} size="invisible" ref={recaptchaRef} />

      {step === "PHONE" ? (
        <>
          <div className={styles.heading}>Login with phone number</div>
          <div className={styles.fieldWrapper}>
            <Textfield
              label="Phone Number"
              type="tel"
              autoComplete="tel"
              fullWidth
              margin="normal"
              value={phone}
              onChange={(event) => {
                const digitsOnly = (event.target.value || "")
                  .replace(/\D/g, "")
                  .slice(0, maxPhoneLength);
                setPhone(digitsOnly);
              }}
            />
          </div>
          <div className={styles.fieldWrapper}>
            <PrimaryButton
              onClick={() =>
                handleGenerateOtpClick({
                  phone,
                  isdCode: LOGIN_ISD_CODE,
                  recaptchaRef,
                  setStatus,
                  generateOtp,
                  openSnackbar,
                  setStep,
                  restart,
                })
              }
              disabled={!canRequestOtp}
              fullWidth
            >
              {isOtpLoading ? "Loading..." : "Request OTP"}
            </PrimaryButton>
          </div>

          {status && <div className={styles.errorText}>{status}</div>}
        </>
      ) : (
        <>
          <ArrowBackIosIcon
            className={styles.goBackIcon}
            onClick={() => {
              setStep("PHONE");
              setOtp("");
              setStatus("");
            }}
          />

          <div className={styles.heading}>OTP Verification</div>
          <div className={styles.subHeading}>
            We have sent an OTP to <b>{displayPhone}</b>
          </div>

          <div className={styles.fieldWrapper}>
            <OtpInput
              value={otp}
              onChange={setOtp}
              numInputs={4}
              isInputNum
              renderSeparator={<span>-</span>}
              renderInput={(props) => (
                <input {...props} className={styles.otpInput} />
              )}
              containerClassName={styles.otpContainer}
            />
          </div>

          <div className={styles.resendWrapper}>
            {seconds > 0 ? (
              <div className={styles.resendOtpText}>
                Resend OTP in {seconds}s
              </div>
            ) : (
              <div
                onClick={() =>
                  handleGenerateOtpClick({
                    phone,
                    isdCode: LOGIN_ISD_CODE,
                    recaptchaRef,
                    setStatus,
                    generateOtp,
                    openSnackbar,
                    setStep,
                    restart,
                  })
                }
                className={styles.resendOtpLink}
              >
                Resend OTP
              </div>
            )}
          </div>
          <div className={styles.fieldWrapper}>
            <PrimaryButton
              onClick={() =>
                handleLoginClick({
                  otp,
                  phone,
                  isdCode: LOGIN_ISD_CODE,
                  setStatus,
                  loginWithOtp,
                  authLogin,
                  navigate,
                  openSnackbar,
                })
              }
              disabled={isLoggingIn || otp.trim().length < 4}
              fullWidth
            >
              {isLoggingIn ? "Loading..." : "Verify & Login"}
            </PrimaryButton>
          </div>

          {status && <div className={styles.errorText}>{status}</div>}
        </>
      )}
    </div>
  );
};

export default OtpLogin;
